import { useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { buildPermissionGroups, formatPermissionLabel, type PermissionGroupView } from "../constants/permissionLabels";

interface PermissionDto {
  id: string;
  code: string;
  roleId: string;
}

interface RoleSummary {
  id: string;
  name: string;
}

export default function RoleList({ reloadKey }: { reloadKey: number }) {
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editedName, setEditedName] = useState("");
  const [permissionsVisible, setPermissionsVisible] = useState<string | null>(null);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionDto[]>([]);
  const MySwal = withReactContent(Swal);

  useEffect(() => {
    void fetchRoles();
    void fetchAllPermissions();
  }, [reloadKey]);

  const fetchRoles = async () => {
    try {
      const res = await axios.get("/roles");
      setRoles(res.data ?? []);
    } catch (error) {
      console.error("[roles] roller alinamadi", error);
      setRoles([]);
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const res = await axios.get("/permission");
      const unique = Array.from(new Map((res.data ?? []).map((p: PermissionDto) => [p.code, p])).values());
      setAllPermissions(unique as PermissionDto[]);
    } catch (error) {
      console.error("[permissions] alinamadi", error);
      setAllPermissions([]);
    }
  };

  const fetchRolePermissions = async (roleId: string) => {
    try {
      const res = await axios.get(`/permission/role/${roleId}`);
      setRolePermissions((res.data ?? []).map((p: PermissionDto) => p.code));
    } catch (error) {
      console.error("[permissions] rol listesi alinamadi", error);
      setRolePermissions([]);
    }
  };

  const togglePermission = async (roleId: string, code: string) => {
    const exists = rolePermissions.includes(code);
    try {
      if (exists) {
        const res = await axios.get(`/permission/role/${roleId}`);
        const matched = (res.data ?? []).find((p: PermissionDto) => p.code === code);
        if (matched) {
          await axios.delete(`/permission/${matched.id}`);
        }
      } else {
        await axios.post("/permission", { roleId, code });
      }
      await fetchRolePermissions(roleId);
    } catch (error) {
      console.error("[permissions] toggle basarisiz", error);
    }
  };

  const deleteRole = async (roleId: string) => {
    const result = await MySwal.fire({
      title: "Emin misin?",
      text: "Bu rolu silmek istedigine emin misin?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Evet, sil",
      cancelButtonText: "Vazgec",
    });

    if (result.isConfirmed) {
      await axios.delete(`/roles/${roleId}`);
      await MySwal.fire("Silindi", "Rol basariyla silindi.", "success");
      void fetchRoles();
    }
  };

  const handleEdit = (roleId: string, currentName: string) => {
    setEditingRoleId(roleId);
    setEditedName(currentName);
  };

  const saveEdit = async (roleId: string) => {
    if (!editedName.trim()) {
      await MySwal.fire("Hata", "Rol adi bos olamaz.", "error");
      return;
    }

    try {
      await axios.put(`/roles/${roleId}`, { id: roleId, name: editedName.trim() });
      await MySwal.fire("Basarili", "Rol adi guncellendi.", "success");
      setEditingRoleId(null);
      void fetchRoles();
    } catch (err) {
      console.error("Guncelleme hatasi:", err);
      await MySwal.fire("Hata", "Rol guncellenirken bir sorun olustu.", "error");
    }
  };

  const togglePermissions = async (roleId: string) => {
    if (permissionsVisible === roleId) {
      setPermissionsVisible(null);
      return;
    }
    setPermissionsVisible(roleId);
    await fetchRolePermissions(roleId);
  };

  const cloneRole = async (roleId: string, roleName: string) => {
    const { isConfirmed, value } = await MySwal.fire({
      title: "Rolü klonla",
      input: "text",
      inputLabel: "Yeni rol adı",
      inputValue: `${roleName} Kopya`,
      showCancelButton: true,
      confirmButtonText: "Klonla",
      cancelButtonText: "Vazgeç",
      reverseButtons: true,
      inputValidator: (val) => (!val || !val.trim() ? "Rol adı boş olamaz." : null),
    });
    const newName = isConfirmed && typeof value === "string" ? value : "";
    if (!newName || !newName.trim()) {
      return;
    }

    try {
      const createRes = await axios.post("/roles", { name: newName.trim() });
      const newRoleId = createRes.data?.id ?? createRes.data?.Id;
      if (!newRoleId) {
        throw new Error("Rol oluşturulamadı.");
      }

      await fetchRolePermissions(roleId);
      await Promise.all(
        rolePermissions.map((code) =>
          axios.post("/permission", {
            roleId: newRoleId,
            code,
          }),
        ),
      );
      await MySwal.fire("Başarılı", "Rol klonlandı.", "success");
      setPermissionsVisible(null);
      setRolePermissions([]);
      await fetchRoles();
    } catch (error) {
      console.error("[roles] clone failed", error);
      await MySwal.fire("Hata", "Rol klonlanamadı.", "error");
    }
  };

  const permissionGroups: PermissionGroupView[] = useMemo(
    () => buildPermissionGroups(allPermissions.map((perm) => perm.code)),
    [allPermissions],
  );

  return (
    <div className="rounded bg-white p-4 shadow">
      <h2 className="mb-4 text-lg font-semibold">Roller</h2>
      <ul className="space-y-4">
        {roles.map((role) => (
          <li key={role.id} className="rounded border p-3 shadow-sm">
            {editingRoleId === role.id ? (
              <div className="flex items-center gap-2">
                <input className="border p-1" value={editedName} onChange={(e) => setEditedName(e.target.value)} />
                <button onClick={() => saveEdit(role.id)} className="rounded bg-green-500 px-2 py-1 text-white">
                  Kaydet
                </button>
                <button onClick={() => setEditingRoleId(null)} className="rounded bg-gray-300 px-2 py-1">
                  Vazgec
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <strong>{role.name}</strong>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(role.id, role.name)}
                    className="rounded bg-amber-400 px-2 py-1 text-sm text-white"
                  >
                    Duzenle
                  </button>
                  <button
                    onClick={() => cloneRole(role.id, role.name)}
                    className="rounded bg-emerald-500 px-2 py-1 text-sm text-white"
                    title="Rolu klonla"
                  >
                    Klonla
                  </button>
                  <button
                    onClick={() => togglePermissions(role.id)}
                    className="rounded bg-blue-500 px-2 py-1 text-sm text-white"
                  >
                    Izinler
                  </button>
                  <button
                    onClick={() => deleteRole(role.id)}
                    className="rounded bg-red-500 px-2 py-1 text-sm text-white"
                  >
                    Sil
                  </button>
                </div>
              </div>
            )}

            {permissionsVisible === role.id && (
              <div className="mt-3 space-y-4">
                {permissionGroups.length === 0 && (
                  <div className="text-sm text-gray-500">Sistemde tanimli izin bulunamadi.</div>
                )}
                {permissionGroups.map((group) => (
                  <div key={group.title}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{group.title}</div>
                    <div className="mt-2 grid gap-1 sm:grid-cols-2">
                      {group.items.map((perm) => (
                        <label
                          key={perm.code}
                          className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={rolePermissions.includes(perm.code)}
                            onChange={() => togglePermission(role.id, perm.code)}
                          />
                          <span>{formatPermissionLabel(perm.code)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
